import { isFuture, parse } from "date-fns";
import { useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useLocalStorage } from "react-use";

import { ARBITRUM } from "config/chains";
import {
  AL16Z_DELISTING_EVENT_ID,
  EventData,
  OM_MANTRA_MIGRATION_EVENT_ID,
  WELL_DELISTING_EVENT_ID,
  appEventsData,
  homeEventsData,
} from "config/events";
import useIncentiveStats from "domain/synthetics/common/useIncentiveStats";
import { useMarketsInfoRequest } from "domain/synthetics/markets";
import { usePositions } from "domain/synthetics/positions";
import { useTokensDataRequest } from "domain/synthetics/tokens";
import { useUiFlagsRequest } from "domain/synthetics/uiFlags/useUiFlagsRequest";
import { useChainId } from "lib/chains";
import { isHomeSite } from "lib/legacy";
import useWallet from "lib/wallets/useWallet";

import EventToast from "./EventToast";

const AL16Z_MARKET_ADDRESS = "0xD60f1BA6a76979eFfE706BF090372Ebc0A5bF169";
const OM_MARKET_ADDRESS = "0x89EB78679921499632fF16B1be3ee48295cfCD91";
const WELL_MARKET_ADDRESS = "0x2347EbB8645Cc2EA0Ba92D1EC59704031F2fCCf4";

function useEventToast() {
  const isHome = isHomeSite();
  const [visited, setVisited] = useLocalStorage<string[]>("visited-announcements", []);
  const { chainId, srcChainId } = useChainId();
  const { tokensData } = useTokensDataRequest(chainId, srcChainId);
  const { marketsInfoData } = useMarketsInfoRequest(chainId, { tokensData });
  const arbIncentiveStats = useIncentiveStats(ARBITRUM);

  const isAdaptiveFundingActiveSomeMarkets = useMemo(() => {
    if (!marketsInfoData) return;
    return Object.values(marketsInfoData).some((market) => market.fundingIncreaseFactorPerSecond > 0);
  }, [marketsInfoData]);

  const isAdaptiveFundingActiveAllMarkets = useMemo(() => {
    if (!marketsInfoData) return;
    return Object.values(marketsInfoData)
      .filter((market) => !market.isSpotOnly)
      .every((market) => market.fundingIncreaseFactorPerSecond > 0);
  }, [marketsInfoData]);

  const { account } = useWallet();

  const positions = usePositions(chainId, {
    marketsData: marketsInfoData,
    tokensData: tokensData,
    account: account,
  });

  const hasAl16ZPosition = useMemo(() => {
    return Object.values(positions.positionsData ?? {}).some(
      (position) => position.marketAddress === AL16Z_MARKET_ADDRESS
    );
  }, [positions.positionsData]);

  const hasOmPosition = useMemo(() => {
    return Object.values(positions.positionsData ?? {}).some(
      (position) => position.marketAddress === OM_MARKET_ADDRESS
    );
  }, [positions.positionsData]);

  const hasWellPosition = useMemo(() => {
    return Object.values(positions.positionsData ?? {}).some(
      (position) => position.marketAddress === WELL_MARKET_ADDRESS
    );
  }, [positions.positionsData]);

  const { uiFlags } = useUiFlagsRequest();

  useEffect(() => {
    const someIncentivesOn = Boolean(arbIncentiveStats?.lp?.isActive || arbIncentiveStats?.trading?.isActive);
    const validationParams: Record<string, boolean | undefined> = {
      "v2-adaptive-funding": isAdaptiveFundingActiveSomeMarkets,
      "v2-adaptive-funding-coming-soon":
        isAdaptiveFundingActiveSomeMarkets !== undefined && !isAdaptiveFundingActiveSomeMarkets,
      "v2-adaptive-funding-all-markets": isAdaptiveFundingActiveAllMarkets,
      "arbitrum-incentives-launch-2": someIncentivesOn,
    };
    const eventsData = isHome ? homeEventsData : appEventsData;

    eventsData
      .filter((event) => event.id !== AL16Z_DELISTING_EVENT_ID || hasAl16ZPosition)
      .filter((event) => event.id !== OM_MANTRA_MIGRATION_EVENT_ID || hasOmPosition)
      .filter((event) => event.id !== WELL_DELISTING_EVENT_ID || hasWellPosition)
      .filter((event) => isEventActive(event, uiFlags))
      .filter(
        (event) => !event.startDate || !isFuture(parse(event.startDate + ", +00", "d MMM yyyy, H:mm, x", new Date()))
      )
      .filter((event) => isFuture(parse(event.endDate + ", +00", "d MMM yyyy, H:mm, x", new Date())))
      .filter((event) => Array.isArray(visited) && !visited.includes(event.id))
      .filter((event) => !event.chains || event.chains.includes(chainId))
      .filter((event) => !(event.id in validationParams) || validationParams[event.id])
      .forEach((event) => {
        toast.custom(
          (t) => (
            <EventToast
              event={event}
              id={event.id}
              toast={t}
              variant={event.variant}
              onClick={() => {
                toast.dismiss(event.id);
                const newVisited = visited ? [...visited, event.id] : [event.id];
                setVisited(newVisited);
              }}
            />
          ),
          {
            id: event.id,
            style: {},
          }
        );
      });
  }, [
    visited,
    setVisited,
    isHome,
    chainId,
    isAdaptiveFundingActiveSomeMarkets,
    isAdaptiveFundingActiveAllMarkets,
    arbIncentiveStats,
    hasAl16ZPosition,
    hasOmPosition,
    hasWellPosition,
    uiFlags,
  ]);
}

function isEventActive(event: EventData, uiFlags: Record<string, boolean> | undefined): boolean {
  if (event.flagId !== undefined) {
    return uiFlags?.[event.flagId] === true;
  }

  return event.isActive === true;
}

export default useEventToast;
